import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle, FlexContainer } from '../../styles/Layout';
import { FaChartLine, FaUniversity, FaUsers, FaCubes, FaArrowRight, FaRocket, FaCode, FaMobile } from 'react-icons/fa';

const ServicesSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(0, 255, 136, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
    z-index: 1;
  }
`;

const ServiceTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
  gap: var(--spacing-md);
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ServiceTab = styled(motion.div)`
  padding: 1.2rem 2rem;
  cursor: pointer;
  border-radius: 16px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  ${props => props.active ? `
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    color: white;
    box-shadow: 0 8px 32px rgba(0, 212, 255, 0.3);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      animation: shimmer 2s infinite;
    }
  ` : `
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
    
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
      transform: translateY(-3px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
      border-color: var(--neon-blue);
    }
  `}
  
  svg {
    margin-right: 8px;
    font-size: 1.2rem;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  
  @media (max-width: 768px) {
    width: 80%;
    justify-content: center;
  }
`;

const ServiceContent = styled(motion.div)`
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
`;

const ServiceProject = styled(FlexContainer)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5);
  }
`;

const ProjectImage = styled.div`
  flex: 1;
  min-height: 500px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.gradient || 'linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(255, 0, 128, 0.3) 100%)'};
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const ViewButton = styled(motion.a)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%);
  backdrop-filter: blur(12px);
  color: var(--primary-dark);
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  text-decoration: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  svg {
    margin-left: 8px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: scale(1.05);
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const ProjectInfo = styled.div`
  flex: 1;
  padding: var(--spacing-xl);
  
  h3 {
    margin-bottom: var(--spacing-lg);
    font-size: 2.2rem;
    background: linear-gradient(135deg, var(--text-primary) 0%, var(--neon-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    margin-bottom: var(--spacing-lg);
    line-height: 1.8;
    font-size: 1.1rem;
    color: var(--text-secondary);
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
`;

const TechTag = styled.span`
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, ${props => props.color || 'var(--neon-blue)'}80 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 4px 15px ${props => props.color || 'var(--neon-blue)'}40;
`;

const KeyFeatures = styled.ul`
  list-style-type: none;
  margin-top: var(--spacing-lg);
  
  li {
    position: relative;
    padding-left: 30px;
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
    line-height: 1.6;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--neon-green) 0%, var(--neon-blue) 100%);
      box-shadow: 0 0 10px var(--neon-green);
    }
  }
`;

const Services = () => {
  const [activeTab, setActiveTab] = useState('trading');
  
  const services = [
    {
      id: 'trading',
      name: 'Investoday',
      title: 'Algorithmic Trading Platform',
      icon: <FaChartLine />,
      description: 'An advanced trading platform enabling users to create, test, and deploy algorithmic trading strategies with real-time market data integration and broker API connectivity. Built with cutting-edge technology for professional traders.',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      gradient: 'linear-gradient(135deg, rgba(0, 255, 136, 0.4) 0%, rgba(0, 212, 255, 0.4) 100%)',
      techStack: ['React', 'Node.js', 'WebSocket', 'PostgreSQL', 'Redis', 'Docker'],
      techColors: ['var(--neon-blue)', 'var(--neon-green)', 'var(--neon-purple)', 'var(--neon-magenta)'],
      features: [
        'Real-time data feeds from multiple exchanges',
        'Advanced algorithmic trading strategies',
        'Comprehensive backtesting framework',
        'Risk management and portfolio optimization',
        'Professional-grade charting and analytics'
      ]
    },
    {
      id: 'erp',
      name: 'Educational ERP',
      title: 'Smart Campus Management',
      icon: <FaUniversity />,
      description: 'A comprehensive ERP solution designed specifically for educational institutions, managing the complete student lifecycle from admissions to alumni relations, with integrated fee management and academic planning.',
      image: 'https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      gradient: 'linear-gradient(135deg, rgba(255, 255, 0, 0.4) 0%, rgba(255, 165, 0, 0.4) 100%)',
      techStack: ['React', 'Express.js', 'MongoDB', 'Firebase', 'PWA', 'Chart.js'],
      techColors: ['var(--neon-yellow)', 'var(--neon-green)', 'var(--neon-blue)', 'var(--neon-purple)'],
      features: [
        'Complete student lifecycle management',
        'Automated fee collection and tracking',
        'Multi-tenant architecture for scalability',
        'Real-time parent-teacher communication',
        'Advanced analytics and reporting dashboard'
      ]
    },
    {
      id: 'job',
      name: 'EAAS Job Portal',
      title: 'AI-Powered Recruitment',
      icon: <FaUsers />,
      description: 'A sophisticated job portal connecting employers and job seekers with advanced AI matching, real-time notifications, and integrated hiring workflow management for streamlined recruitment processes.',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      gradient: 'linear-gradient(135deg, rgba(0, 212, 255, 0.4) 0%, rgba(139, 92, 246, 0.4) 100%)',
      techStack: ['Vue.js', 'Laravel', 'MySQL', 'Elasticsearch', 'AI/ML', 'WebRTC'],
      techColors: ['var(--neon-blue)', 'var(--neon-purple)', 'var(--neon-green)', 'var(--neon-magenta)'],
      features: [
        'AI-powered candidate matching algorithms',
        'Real-time chat and video interviews',
        'Advanced filtering and search capabilities',
        'Automated application tracking system',
        'Comprehensive analytics for recruiters'
      ]
    },
    {
      id: 'property',
      name: 'More Alpha',
      title: 'Blockchain Property Platform',
      icon: <FaCubes />,
      description: 'A blockchain-powered real estate platform leveraging Solana for transparent property transactions, with tokenized rewards for platform activities and decentralized verification of property records.',
      image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(255, 0, 128, 0.4) 100%)',
      techStack: ['React', 'Solana', 'Rust', 'Web3.js', 'IPFS', 'GraphQL'],
      techColors: ['var(--neon-purple)', 'var(--neon-magenta)', 'var(--neon-blue)', 'var(--neon-green)'],
      features: [
        'Solana blockchain integration for transparency',
        'NFT-based property ownership certificates',
        'Decentralized property verification system',
        'Crypto payment processing and escrow',
        'Smart contracts for automated transactions'
      ]
    }
  ];
  
  const currentService = services.find(service => service.id === activeTab);
  
  return (
    <ServicesSection id="services">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>Services Portfolio</SectionTitle>
          <SectionSubtitle>
            Explore our diverse range of innovative digital solutions crafted with premium design 
            and cutting-edge technology for different industries and business needs.
          </SectionSubtitle>
        </motion.div>
        
        <ServiceTabs>
          {services.map((service, index) => (
            <ServiceTab 
              key={service.id}
              active={activeTab === service.id}
              onClick={() => setActiveTab(service.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {service.icon} {service.name}
            </ServiceTab>
          ))}
        </ServiceTabs>
        
        <AnimatePresence mode="wait">
          {currentService && (
            <ServiceContent
              key={currentService.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
            >
              <ServiceProject direction="row" mobileDirection="column">
                <ProjectImage image={currentService.image}>
                  <ProjectOverlay>
                    <ViewButton 
                      href="#portfolio"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      View Details <FaArrowRight />
                    </ViewButton>
                  </ProjectOverlay>
                </ProjectImage>
                
                <ProjectInfo>
                  <h3>{currentService.title}</h3>
                  <p>{currentService.description}</p>
                  
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-md)' }}>
                    Technology Stack:
                  </h4>
                  <TechStack>
                    {currentService.techStack.map((tech, index) => (
                      <TechTag 
                        key={index} 
                        color={currentService.techColors[index % currentService.techColors.length]}
                      >
                        {tech}
                      </TechTag>
                    ))}
                  </TechStack>
                  
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-md)' }}>
                    Key Features:
                  </h4>
                  <KeyFeatures>
                    {currentService.features.map((feature, index) => (
                      <motion.li 
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        {feature}
                      </motion.li>
                    ))}
                  </KeyFeatures>
                </ProjectInfo>
              </ServiceProject>
            </ServiceContent>
          )}
        </AnimatePresence>
      </Container>
    </ServicesSection>
  );
};

export default Services;